import React from 'react';
import { Handles, Rail, Slider, Ticks, Tracks } from 'react-compound-slider';
import { Handle } from './Handle';
import Track, {
  sliderProgress,
} from '@teetsh/app/src/components/Form/Slider/Track';
import { Tick } from './Tick';
import { replaceItemAtIndex } from '@teetsh/app/src/utils/listMethods';
import tw from 'twin.macro';
import { PourcentagesNotation } from '@teetsh/app/src/utils/notationSystems/notationSystems';
import { borderColors } from '@teetsh/app/src/styles/themeConstants';
import useIsMobile from '@teetsh/app/src/components/ResponsiveSwitcher/useIsMobile';

interface Cursors {
  id: string;
  value: number;
  label: string;
}

interface Props {
  handles: Cursors[];
  onChange: (v: Cursors[]) => void;
  noColors?: boolean;
}

export function MultiSlider(props: Props) {
  const { onChange, noColors = false } = props;
  const isMobile = useIsMobile();
  const tickCount = isMobile ? 5 : 15;

  return (
    <div>
      <Slider
        tw="h-10 outline-none cursor-default w-full relative min-w-[150px] select-none"
        step={1}
        mode={2}
        domain={[0, 100]}
        onChange={v => {
          const handlesValues = props.handles.map(h => h.value);
          const modifiedItemIndex = v.findIndex(
            vv => !handlesValues.includes(vv),
          );
          if (modifiedItemIndex < 0) {
            return;
          }

          onChange(
            replaceItemAtIndex(
              props.handles,
              {
                ...props.handles[modifiedItemIndex],
                value: v[modifiedItemIndex],
              },
              modifiedItemIndex,
            ),
          );
        }}
        values={props.handles.map(h => h.value).filter(h => h >= 0)}
      >
        <Rail>
          {({ getRailProps }) => (
            <div
              css={[sliderProgress, tw`mt-[35px]`]}
              {...(getRailProps() as any)}
            />
          )}
        </Rail>
        <Handles>
          {({ handles, getHandleProps }) => (
            <div className="slider-handles">
              {handles.map(handle => {
                const matchingNote = props.handles.filter(h => h.value >= 0)[
                  parseInt(handle.id.split('-')[1], 10)
                ];
                return (
                  <Handle
                    key={handle.id}
                    note={matchingNote}
                    handle={handle}
                    getHandleProps={getHandleProps}
                    noColors={noColors}
                  />
                );
              })}
            </div>
          )}
        </Handles>
        <Tracks right={false}>
          {({ tracks, getTrackProps }) => (
            <div className="slider-tracks">
              {tracks.map(({ id, source, target }) => (
                <Track
                  key={id}
                  source={source}
                  target={target}
                  getTrackProps={getTrackProps}
                  bgColor={
                    noColors
                      ? borderColors['neutral-moderate']
                      : PourcentagesNotation.getNoteColorContrast(target.value)
                  }
                />
              ))}
            </div>
          )}
        </Tracks>
        <Ticks
          count={
            tickCount /* generate 5 ticks on mobile (every 20), 15 on desktop */
          }
        >
          {({ ticks }) => (
            <div className="slider-ticks">
              {ticks.map(tick => (
                <Tick key={tick.id} tick={tick} count={ticks.length} />
              ))}
            </div>
          )}
        </Ticks>
      </Slider>
    </div>
  );
}
